/* BEGIN_COPYRIGHT_HEADER

Copyright Vspry International Limited (c) 2020
All rights reserved.

END_COPYRIGHT_HEADER */
import { useState } from 'react'
import Swal from 'sweetalert2'
import { useLocation, useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { isAndroid } from 'react-device-detect'
import { FlexBox, PopIcon, Text, ConfirmSwal, Icon } from 'vspry-style-components'
import { VspryMenu } from 'vspry-components'

import { Account, createActivity, Holding, IDCard, isHolding, isIDCard } from 'api/common'

import { useClaims } from 'context/claimsContext'
import { useMessaging } from 'context/messageContext'
import { isDictionaryKey, useLocale } from 'context/localeContext'
import { useAuth } from 'context/authContext'
import { useAppConfig } from 'context/appConfigContext'
import { useRedemptions } from 'context/redemptionsContext'

import useResponsive from 'hooks/responsive'

import {
    hasMerchantRewards,
    hasSubscriptionFeature,
    isConsumer,
    isConsumerMember,
    isMerchantOwner,
    isMerchantSenior,
    isMerchantStaff,
} from 'utils/config'

import { productToRedemptionsTabLabel } from 'views/user/rewards/RedemptionsInbox'

import theme from 'styles/theme'

import { productToActivationTabLabel } from 'components/marketing/OfferActivationsList'
import { tabLabelToTabSearchParam } from 'components/TabbedLayout'
import MobileMenuBar from 'components/style-elements/MobileMenuBar'
import { AppConfig } from 'api/config'

const getPWADisplayMode = () => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
    if (document.referrer.startsWith('android-app://')) {
        return `twa`
    }
    if (('standalone' in navigator && navigator.standalone) || isStandalone) {
        return `standalone`
    }
    return `browser`
}

const MenuIconContainer = styled.div`
    position: fixed;
    top: 10px;
    right: 10px;
    z-index: 9998;
    width: 50px;
    height: 50px;
    border-radius: 100%;
    /* background-color: ${(props) => props.theme.buttonBG}; */
    display: flex;
    justify-content: center;
    align-items: center;

    :hover {
        cursor: pointer;
        background: ${(props) => props.theme.buttonBG}4d;
    }
`

export type NavState = { c: AppConfig; p: Holding | IDCard | null; a: Account | null }
export type NavItem = {
    title: string
    icon: Parameters<typeof Icon>[0]['name']
    to?: string
    onClick?: (state: NavState) => void
    condition: (state: NavState) => boolean
    count?: number
    selected?: boolean
}
export const isNavItem = (obj: unknown): obj is NavItem => {
    if (typeof obj !== 'object' || obj === null) return false
    if (!('title' in obj) || typeof obj.title !== 'string') return false
    if (!('icon' in obj) || typeof obj.icon !== 'string') return false
    if (!('condition' in obj) || typeof obj.condition !== 'function') return false
    if ('onClick' in obj && typeof obj.onClick !== 'function') return false
    if ('count' in obj && typeof obj.count !== 'number') return false
    if ('selected' in obj && typeof obj.selected !== 'boolean') return false
    return true
}

export type NavSection<I = NavItem> = {
    title: string
    icon: Parameters<typeof Icon>[0]['name']
    section?: 'top' | 'bottom'
    collapsible?: boolean
    reverse?: boolean
    items: I[]
}
export const isNavSection = (obj: unknown): obj is NavSection => {
    if (typeof obj !== 'object' || obj === null) return false
    if (!('title' in obj) || typeof obj.title !== 'string') return false
    if (!('icon' in obj) || typeof obj.icon !== 'string') return false
    if ('section' in obj && (typeof obj.section !== 'string' || (obj.section !== 'top' && obj.section !== 'bottom'))) return false
    if ('collapsible' in obj && typeof obj.collapsible !== 'boolean') return false
    if ('reverse' in obj && typeof obj.reverse !== 'boolean') return false
    if (!('items' in obj) || !Array.isArray(obj.items)) return false
    return true
}

function NavDrawer() {
    const [menuOpen, setOpen] = useState(false)
    const { translate } = useLocale()
    const { account, currentProduct, user, setCurrentAccount } = useAuth()
    const { appConfig } = useAppConfig()
    const { unread } = useMessaging()
    const { unread: unreadClaims } = useClaims()
    const { unread: unreadRedemptions } = useRedemptions()
    const navigate = useNavigate()
    const location = useLocation()
    const isMobile = useResponsive([660]) <= 660

    const signOut = async () => {
        setOpen(false)
        return Swal.fire({
            text: translate('swal.signOutConfirm'),
            icon: 'warning',
            confirmButtonText: `<p style="color:${theme.buttonText}">${translate('buttons.signOut')}</p>`,
            confirmButtonColor: theme.buttonBG,
            cancelButtonText: `<p style="color:${theme.disabledButtonText}">${translate('buttons.cancel')}</p>`,
            cancelButtonColor: theme.disabledButtonBG,
            showConfirmButton: true,
            showCancelButton: true,
        }).then((v) => v.isConfirmed && navigate('/signout'))
    }

    const navItems: (NavItem | NavSection<NavItem | NavSection>)[] = [
        { title: `navigation.productHome`, icon: isHolding(currentProduct) ? 'gift' : 'home', to: '/home', condition: () => true },
        {
            title: `navigation.rewardConfig`,
            icon: 'cog',
            to: '/rewards/setup',
            condition: ({ c, a }) => isMerchantSenior(a) && c.assets_enabled.includes('Reward'),
        },
        {
            title: `navigation.identityCards`,
            icon: 'vcard',
            to: '/ids/setup',
            condition: ({ c, a }) => isMerchantSenior(a) && c.assets_enabled.includes('ID Card'),
        },
        {
            title: `navigation.marketing`,
            icon: 'target',
            to: '/marketing',
            condition: ({ c, a }) => c.marketing_enabled && isMerchantStaff(a),
        },
        {
            title: `navigation.healthReferral`,
            icon: 'stethoscope',
            to: '/healthreferral',
            condition: ({ c, a }) => c.health_cover_referral_enabled && isMerchantStaff(a),
        },
        {
            title: isMobile ? `navigation.healthCover` : `navigation.getHealthCover`,
            icon: 'stethoscope',
            to: '/healthreferral',
            condition: ({ c, a }) => c.health_cover_referral_enabled && isConsumerMember(a),
        },
        { title: `navigation.inbox`, icon: 'inbox', to: '/inbox', condition: ({ a }) => !!a, count: unread },
        {
            title: `navigation.rewardClaims`,
            icon: 'star',
            to: '/rewards/claim',
            condition: ({ p, a }) => isConsumer(a) && p?.asset === 'Reward',
        },
        {
            title: `navigation.redeemedRewards`,
            icon: 'gift',
            onClick: ({ p }) =>
                isHolding(p) ? navigate(`/rewards/redemptions?tab=${tabLabelToTabSearchParam(productToRedemptionsTabLabel(p))}`) : null,
            condition: ({ p, a }) => isConsumer(a) && p?.asset === 'Reward',
        },
        {
            title: `navigation.claims`,
            icon: 'star',
            to: '/rewards/claims',
            condition: ({ c, a }) =>
                isMerchantStaff(a) &&
                c.assets_enabled.includes('Reward') &&
                hasMerchantRewards(a) &&
                (!c.merchant_subscriptions_enabled || hasSubscriptionFeature('createrewards', a)),
            count: Object.values(unreadClaims).reduce((t, c) => t + c, 0),
        },
        {
            title: `navigation.checkins`,
            icon: 'compass',
            to: '/checkins',
            condition: ({ c, a }) =>
                isMerchantStaff(a) &&
                c.assets_enabled.includes('Reward') &&
                hasMerchantRewards(a) &&
                (!c.merchant_subscriptions_enabled || hasSubscriptionFeature('checkin', a)),
        },
        {
            title: `navigation.customers`,
            icon: 'address book',
            to: '/rewards/customers',
            condition: ({ c, a }) =>
                isMerchantStaff(a) &&
                c.assets_enabled.includes('Reward') &&
                hasMerchantRewards(account) &&
                (!c.merchant_subscriptions_enabled || hasSubscriptionFeature('createrewards', a)),
        },
        {
            title: `navigation.chargeCustomer`,
            icon: 'money bill alternate',
            to: '/createbill',
            condition: ({ p, a }) => isMerchantStaff(a) && !!p?.asset.includes('Merchant'),
        },
        {
            title: `navigation.redemptions`,
            icon: 'thumbtack',
            to: '/rewards/redemptions',
            condition: ({ c, a }) =>
                isMerchantStaff(a) &&
                c.assets_enabled.includes('Reward') &&
                hasMerchantRewards(account) &&
                (!c.merchant_subscriptions_enabled || hasSubscriptionFeature('createrewards', a)),
            count: Object.values(unreadRedemptions).reduce((t, c) => t + c, 0),
        },
        {
            title: `navigation.activations.merchant`,
            icon: 'play circle outline',
            to: '/marketing/activations',
            condition: ({ c, a, p }) => c.marketing_enabled && isMerchantStaff(a) && !!p,
        },
        {
            title: `navigation.activations.consumer`,
            icon: 'play circle outline',
            onClick: ({ p }) => (isHolding(p) ? navigate(`/activations?tab=${tabLabelToTabSearchParam(productToActivationTabLabel(p))}`) : null),
            condition: ({ c, a }) => c.marketing_enabled && isConsumer(a),
        },
        {
            title: `navigation.referFriend`,
            icon: 'share alternate',
            onClick: ({ c, p }) => {
                navigator
                    .share({
                        url: `/#/referral?product=${p?.productId}&referrer=${p?.id}`,
                        text: c.consumer_refer_text?.replace(/{{product}}/g, p?.product ?? ''),
                        title: c.consumer_refer_title,
                    })
                    .catch((e) => console.warn(e))
            },
            condition: ({ c, p, a }) => !!c.consumer_refer_title && !!c.consumer_refer_text && !!p && isConsumer(a),
        },
        {
            title: `navigation.referFriend`,
            icon: 'share alternate',
            onClick: ({ c }) =>
                navigator
                    .share({
                        url: user ? `/#/referral?referrer=${user.id}` : '/#/referral',
                        text: c.merchant_refer_text,
                        title: c.merchant_refer_title,
                    })
                    .catch((e) => console.warn(e)),
            condition: ({ c, a }) => !!c.merchant_refer_title && !!c.merchant_refer_text && isMerchantStaff(a),
        },
        {
            title: `navigation.call`,
            icon: 'phone',
            onClick: ({ p, a }) => {
                if (a && isHolding(p)) {
                    createActivity({
                        accountID: a.id,
                        ...(isHolding(p) ? { holdingID: p.id } : {}),
                        ...(isIDCard(p) ? { idCardID: p.id } : {}),
                        to: `tel:${p.bookingPhone}`,
                        from: window.location.href,
                        type: 'User call merchant',
                    })
                    window.open(`tel:${p.bookingPhone}`)
                }
            },
            condition: ({ p, a }) => isConsumer(a) && isHolding(p) && !!p.bookingPhone,
        },
        {
            title: `navigation.book`,
            icon: 'calendar alternate outline',
            onClick: ({ p, a }) =>
                a &&
                isHolding(p) &&
                ConfirmSwal.fire({
                    title: translate('swal.book.title'),
                    text: translate('swal.book.text'),
                    iconColor: theme.text,
                    confirmButtonColor: theme.primaryOBJ,
                }).then((r) => {
                    if (r?.value) {
                        createActivity({
                            accountID: a.id,
                            ...(isHolding(p) ? { holdingID: p.id } : {}),
                            ...(isIDCard(p) ? { idCardID: p.id } : {}),
                            to: p.bookingURL ?? '',
                            from: window.location.href,
                            type: 'User book merchant',
                        })
                        window.open(p.bookingURL, '_blank')
                    }
                }),
            condition: ({ p, a }) => isConsumer(a) && isHolding(p) && !!p.bookingURL,
        },
        {
            title: `buttons.pay`,
            icon: 'money bill alternate outline',
            to: '/paymerchant',
            condition: ({ p, a }) => isConsumer(a) && p?.asset === 'Reward',
        },
        {
            title: `navigation.cardSettings`,
            icon: 'cog',
            to: '/profile/home-settings',
            condition: ({ p, a }) => isHolding(p) && !isMerchantStaff(a),
        },
        ...(appConfig
            ? appConfig.external_navigations.map(
                  (n): NavItem => ({
                      title: n.label,
                      to: n.url,
                      icon: n.iconName as Parameters<typeof Icon>[0]['name'],
                      condition: ({ a }) =>
                          (n.audience === 'Consumers' && isConsumer(a)) ||
                          (n.audience === 'Merchants' && isMerchantStaff(a)) ||
                          n.audience === 'Both',
                  })
              )
            : []),
        {
            title: `navigation.documents`,
            icon: 'archive',
            to: '/documents',
            condition: ({ c, a }) => c.document_store_enabled && isConsumerMember(a),
        },
        {
            title: `navigation.companyRecords`,
            icon: 'archive',
            items: [
                {
                    title: `navigation.companyRecords.bankAccounts`,
                    icon: 'briefcase',
                    to: '/documents/bankaccounts',
                    condition: ({ c, a }) => c.business_loans_enabled && isMerchantSenior(a),
                },
                {
                    title: `navigation.companyRecords.financialStatements`,
                    icon: 'chart bar',
                    to: '/documents/financialstatements',
                    condition: ({ c, a }) => c.business_loans_enabled && isMerchantSenior(a),
                },
                {
                    title: `navigation.companyRecords.registrationDocuments`,
                    icon: 'address card',
                    to: '/documents/registrationdocuments',
                    condition: ({ c, a }) => c.business_loans_enabled && isMerchantSenior(a),
                },
            ],
            section: `top`,
            collapsible: true,
            reverse: false,
        },
        {
            title: `navigation.payments`,
            icon: 'payment',
            items: [
                {
                    title: `navigation.directCredit`,
                    to: '/directcredit',
                    icon: 'calendar plus',
                    condition: ({ c, p }) =>
                        c.direct_credit_enabled && ((p?.asset === 'Loan' && p?.balance !== 0) || p?.asset === 'Saving' || p?.asset === 'Laybuy'),
                },
                {
                    title: `navigation.directDebit`,
                    to: '/directdebit',
                    icon: 'calendar alternate',
                    condition: ({ c, p }) =>
                        ((p?.asset === 'Loan' && p?.balance !== 0) || p?.asset === 'Saving' || p?.asset === 'Laybuy') && c.direct_debit_enabled,
                },
                {
                    title: `navigation.directDebits`,
                    to: '/directdebit/history',
                    icon: 'history',
                    condition: ({ c, p }) =>
                        ((p?.asset === 'Loan' && p?.balance !== 0) || p?.asset === 'Saving' || p?.asset === 'Laybuy') && c.direct_debit_enabled,
                },
                {
                    title: `navigation.reimbursementapplication`,
                    icon: 'reply',
                    to: '/reimbursementapplication',
                    condition: ({ c, p }) => p?.asset === 'Saving' && c.reimbursement_enabled,
                },
                {
                    title: `navigation.payoutApplication`,
                    icon: 'share',
                    to: '/payoutapplication',
                    condition: ({ c, p }) => p?.asset === 'Loan' && p?.balance !== 0 && c.loan_payout_enabled,
                },
            ],
            section: `top`,
            collapsible: true,
            reverse: false,
        },
        {
            title: `navigation.addProduct`,
            icon: 'add circle',
            items: [
                {
                    title: `navigation.loanApplication`,
                    icon: 'add',
                    to: '/loans/apply',
                    condition: ({ c, a }) => c.assets_enabled.includes('Loan') && isConsumerMember(a),
                },
                {
                    title: `navigation.hsa`,
                    icon: 'dollar sign',
                    to: '/referral?asset=Saving',
                    condition: ({ c, a }) => c.assets_enabled.includes('Saving') && c.addProduct_enabled && isConsumerMember(a),
                },
                {
                    title: `navigation.hsa`,
                    icon: 'dollar sign',
                    to: '/referral?asset=Merchant',
                    condition: ({ c, a }) => c.assets_enabled.includes('Merchant') && c.addProduct_enabled && isMerchantOwner(a),
                },
                {
                    title: `navigation.addOtherProduct`,
                    icon: 'ellipsis horizontal',
                    to: '/addproduct',
                    condition: ({ c, a }) => c.addProduct_enabled && (isConsumerMember(a) || isMerchantOwner(a)),
                },
            ],
            section: `top`,
            collapsible: true,
            reverse: false,
        },
        {
            title: `navigation.account`,
            icon: 'user circle',
            items: [
                { title: `navigation.signOut`, icon: 'sign-out', condition: ({ a }) => !!a, onClick: () => signOut() },
                ...((user?.accounts?.length ?? 0) > 1
                    ? [
                          {
                              title: `navigation.accounts`,
                              icon: 'users',
                              items:
                                  user?.accounts.map(
                                      (a): NavItem => ({
                                          title: a.accountName,
                                          icon: 'user',
                                          condition: () => true,
                                          onClick: () => setCurrentAccount(a),
                                          selected: account?.id === a.id,
                                      })
                                  ) ?? [],
                              collapsible: true,
                          } as NavSection,
                      ]
                    : []),
                { title: `navigation.help`, icon: 'help circle', to: '/help', condition: () => true },
                {
                    title: `navigation.howToVideos`,
                    icon: 'play circle',
                    to: '/howto',
                    condition: ({ c, a }) =>
                        (isMerchantStaff(a) && c.how_to_videos.some((v) => v.audience === 'Merchant')) ||
                        (isConsumer(a) && c.how_to_videos.some((v) => v.audience === 'Consumer')) ||
                        c.how_to_videos.some((v) => v.audience === 'All'),
                },
                {
                    title:
                        getPWADisplayMode() === 'browser' && isAndroid && window.configuration['PLAY_STORE_APP_ID']
                            ? `navigation.downloadApp`
                            : `navigation.addToHome`,
                    icon: 'download',
                    onClick: () => document.dispatchEvent(new Event('refireinstallprompt', { bubbles: true })),
                    condition: () => getPWADisplayMode() === 'browser',
                },
                { title: `navigation.contactUs`, icon: 'call', to: '/contact', condition: ({ c }) => c.contact_us_menu_enabled },
                { title: `navigation.profile`, icon: 'cogs', to: '/profile', condition: () => true },
                {
                    title: `navigation.plan`,
                    icon: 'clipboard list',
                    to: 'plans',
                    condition: ({ c, a }) => c.merchant_subscriptions_enabled && isMerchantSenior(a),
                },
            ],
            section: `bottom`,
            collapsible: true,
        },
    ]

    const mobileNavItems = {
        Reward: [`navigation.productHome`, `navigation.healthCover`, `navigation.inbox`, `navigation.referFriend`],
        'Merchant Rewards': [`navigation.productHome`, `navigation.rewardConfig`, `navigation.inbox`, `navigation.claims`],
        'ID Card': [`navigation.inbox`, `navigation.activations.consumer`, `navigation.productHome`, `navigation.profile`],
        'Merchant None': [
            ...(appConfig.merchant_identity_card_enabled ? [`navigation.identityCards`] : []),
            `navigation.inbox`,
            `navigation.productHome`,
            `navigation.marketing`,
        ],
        None: [`navigation.inbox`, `navigation.productHome`],
    }

    const handleNavClick = async (to?: string, onClick?: (state: NavState) => void, fast = false) => {
        setOpen(false)
        return setTimeout(
            () => {
                if (to) {
                    if (to.startsWith('http')) window.open(to, '_blank')
                    else navigate(to)
                } else if (onClick) {
                    onClick({ c: appConfig, p: currentProduct, a: account })
                }
            },
            fast ? 0 : 400
        )
    }

    const shouldRender = (itemOrSection: NavItem | NavSection<NavItem | NavSection> | NavSection) => {
        if (isNavItem(itemOrSection)) return itemOrSection.condition({ c: appConfig, p: currentProduct, a: account })
        if (isNavSection(itemOrSection))
            return itemOrSection.items.some((i) =>
                isNavSection(i)
                    ? i.items.some((k) => k.condition({ c: appConfig, p: currentProduct, a: account }))
                    : i.condition({ c: appConfig, p: currentProduct, a: account })
            )
        return false
    }

    const renderItem = ({ title, to, onClick, ...rest }: NavItem) => (
        <VspryMenu.Item
            key={title}
            onClick={() => handleNavClick(to ?? undefined, onClick ?? undefined)}
            selected={location.pathname === to}
            {...rest}
        >
            {isDictionaryKey(title) ? translate(title) : title}
        </VspryMenu.Item>
    )

    const renderSection = ({ title, items, ...rest }: NavSection | NavSection<NavItem | NavSection>) => (
        <VspryMenu.Section key={title} label={isDictionaryKey(title) ? translate(title) : title} menuOpen={menuOpen} {...rest}>
            <FlexBox $column gap='xSmall'>
                {items.filter(shouldRender).map((i) => (isNavSection(i) ? renderSection(i) : renderItem(i)))}
            </FlexBox>
        </VspryMenu.Section>
    )

    return (
        <>
            {!isMobile && (
                <>
                    <VspryMenu open={menuOpen} onClose={() => setOpen(false)} title={<Text size='large'>{translate('pages.home.navigation')}</Text>}>
                        <FlexBox $column $grow>
                            <FlexBox $column gap='xSmall'>
                                {navItems
                                    .filter((e) => isNavItem(e) || e.section === 'top')
                                    .filter(shouldRender)
                                    .map((e) => (isNavItem(e) ? renderItem(e) : renderSection(e)))}
                            </FlexBox>
                            <FlexBox $column gap='xSmall'>
                                {navItems
                                    .filter((e) => isNavSection(e) && e.section === 'bottom')
                                    .filter(shouldRender)
                                    .map((e) => (isNavItem(e) ? renderItem(e) : renderSection(e)))}
                            </FlexBox>
                        </FlexBox>
                    </VspryMenu>
                    <MenuIconContainer onClick={() => setOpen(true)}>
                        <PopIcon
                            color='accent'
                            name='bars'
                            count={
                                unread +
                                Object.values(unreadClaims).reduce((t, c) => t + c, 0) +
                                Object.values(unreadRedemptions).reduce((t, c) => t + c, 0)
                            }
                            popFontColor='primaryOBJText'
                            popColor={theme.buttonBG === theme.primaryOBJ ? theme.error : theme.primaryOBJ}
                            fitted
                            size='big'
                        />
                    </MenuIconContainer>
                </>
            )}
            {isMobile && (
                <MobileMenuBar
                    allItems={navItems.filter(shouldRender).map((i) => (isNavSection(i) ? { ...i, items: i.items.filter(shouldRender) } : i))}
                    displayTitles={
                        mobileNavItems[
                            (currentProduct?.asset as keyof typeof mobileNavItems) ?? (isMerchantStaff(account) ? 'Merchant None' : 'None')
                        ] ?? []
                    }
                    signOut={signOut}
                    handleClick={handleNavClick}
                />
            )}
        </>
    )
}

export default NavDrawer
